// src/pages/OwnerFundsList.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import EmptyState from "../components/EmptyState";
import { apiGet } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { ymdTodayLocal, formatAnyDateToLocale } from "../utils/dateUtils";
import { formatMoney, formatNumber } from "../utils/formatNumber";
import { Button } from "../components/Button";
import { A } from "../styles/appleClasses";
import ListTableShell from "../components/lists/ListTableShell";

type Account = {
  id: number;
  name: string;
  type: string; // 'asset' | 'liability' | 'equity' | ...
  kind?: string; // 'cash' | 'bank' | 'shareholder_advance' | 'due_from_shareholder' | ...
  is_system?: boolean;
};

type JournalRow = {
  date: string; // 'YYYY-MM-DD'
  entry_id: number;
  entry_no: number | null;
  type: string | null;
  source_id?: number | null;
  source: string;
  source_link?: string | null;
  description: string | null;
  account_id: number;
  account: string | number;
  debit: number;
  credit: number;
  voided?: boolean | null;
};

type RowView = {
  id: number; // journal entry id
  date: string;
  description: string;
  amount: number | null;
  bankName: string;
  kind: "advance" | "withdrawal" | "unknown";
};

const ROWS_PER_PAGE = 10;

const KIND_LABELS: Record<RowView["kind"], string> = {
  advance: "Owner Advance",
  withdrawal: "Owner Withdrawal",
  unknown: "—",
};

// ---------- heuristics (when Account.kind not expanded) ----------
const isBankName = (name?: string) =>
  !!name && /cash|bank|checking|savings|petty\s*cash/i.test(name);

function fallbackKindFromName(name?: string): RowView["kind"] {
  if (!name) return "unknown";
  const n = name.toLowerCase();

  if (/shareholder.*advance|owner.*advance|director.*advance/.test(n))
    return "advance";
  if (/due.*from.*(shareholder|owner|director)/.test(n)) return "withdrawal";

  return "unknown";
}

// ---------- sort/group like GeneralJournal ----------
function sortRowsStable(rows: JournalRow[]): JournalRow[] {
  return [...rows].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    const aSeq = a.entry_no ?? a.entry_id;
    const bSeq = b.entry_no ?? b.entry_id;
    if (aSeq !== bSeq) return aSeq - bSeq;

    const aIsDebit = a.debit && a.debit !== 0 ? 1 : 0;
    const bIsDebit = b.debit && b.debit !== 0 ? 1 : 0;
    return bIsDebit - aIsDebit; // debit rows first
  });
}

function groupByEntry(rows: JournalRow[]) {
  const out: { entry_id: number; date: string; group: JournalRow[] }[] = [];

  let prevKey: string | null = null;
  let prevDate: string | null = null;
  let prevEntryId = 0;
  let bucket: JournalRow[] = [];

  for (const r of sortRowsStable(rows)) {
    const seq = r.entry_no ?? r.entry_id;
    const key = `${r.date}::${seq}`;

    if (key !== prevKey || r.date !== prevDate) {
      if (bucket.length)
        out.push({ entry_id: prevEntryId, date: prevDate!, group: bucket });
      bucket = [];
      prevKey = key;
      prevDate = r.date;
      prevEntryId = r.entry_id;
    }

    bucket.push(r);
  }

  if (bucket.length)
    out.push({ entry_id: prevEntryId, date: prevDate!, group: bucket });
  return out;
}

function gjFocusPath(entryId: number, date: string) {
  // Use the journal's explicit same-day range; legacy focus_date is not read.
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || "");
  if (match) {
    const [, yearText, monthText, dayText] = match;
    const year = Number(yearText), month = Number(monthText), day = Number(dayText);
    const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (year > 0 && month >= 1 && month <= 12 && day >= 1 && day <= days[month - 1]) {
      return `/general-journal?start=${date}&end=${date}&focus_entry_id=${entryId}`;
    }
  }
  return `/general-journal?focus_entry_id=${entryId}`;
}

type ListError =
  | { kind: "raw"; text: string }
  | { kind: "local" };

export default function OwnerFundsList() {
  const { t } = useTranslation();
  // KIND_LABELS remains English because it also participates in search.
  const displayKind = (kind: RowView["kind"]) => kind === "advance"
    ? t("ownerFunds.types.advance")
    : kind === "withdrawal" ? t("ownerFunds.types.withdrawal") : KIND_LABELS.unknown;
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  // mimic GeneralJournal date window (this month to today)
  const todayStr = ymdTodayLocal();
  const allTimeStart = "2000-01-01";

  const [start] = useState(allTimeStart);
  const [end] = useState(todayStr);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ListError | null>(null);

  const [accounts, setAccounts] = useState<Account[]>([]);
  const accountById = useMemo(() => {
    const m = new Map<number, Account>();
    for (const a of accounts) m.set(a.id, a);
    return m;
  }, [accounts]);

  const [rows, setRows] = useState<RowView[]>([]);
  const [balances, setBalances] = useState({ advance: 0, withdrawal: 0 });

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const money = (n?: number | null) =>
    formatMoney(typeof n === "number" ? n : null, "$");
  const int = (n: number) => formatNumber(n, { minDp: 0, maxDp: 0 });

  // small Button sizing override (keeps Button.tsx as source of truth)
  const btnSmCls = "px-4";
  const btnSmMin = "0px";

  const searchInputCls = `${A.inputBase} pl-9 pr-4 placeholder:text-gray-400`;

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 active:bg-gray-200 " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD";

  // Load company accounts (company-scoped)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const base = company_id ? `/companies/${company_id}` : "";
        const accs =
          (await apiGet<Account[]>(
            `${base}/accounts?include_system=true&limit=2000`,
          )) ?? (await apiGet<Account[]>(`${base}/accounts`));

        if (!cancelled) setAccounts(accs || []);
      } catch {
        if (!cancelled) setAccounts([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [company_id]);

  // Fetch like GeneralJournal and derive Owner Funds rows
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const gj = await apiGet<JournalRow[]>(
          `/general-journal?start=${start}&end=${end}`,
        );
        const { derivedRows, bal } = deriveOwnerFundsFromGJ(
          gj || [],
          accountById,
        );

        if (!cancelled) {
          setRows(derivedRows);
          setBalances(bal);
        }
      } catch (e: any) {
        if (!cancelled)
          setError(e?.response?.data?.detail
            ? { kind: "raw", text: e.response.data.detail }
            : { kind: "local" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [start, end, accountById]);

  // Search + pagination
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const hay = [
        r.description || "",
        r.bankName || "",
        r.date || "",
        KIND_LABELS[r.kind],
        r.amount != null ? r.amount.toFixed(2) : "",
      ]
        .join(" | ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [rows, search]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    ROWS_PER_PAGE * currentPage,
  );

  useEffect(() => setPage(1), [search, rows.length]);

  const goToGJ = (entryId: number, date: string) => {
    navigate(companyRoute(company_id, gjFocusPath(entryId, date)));
  };

  // Render
  if (loading)
    return (
      <div className="text-center py-6 text-[13px] text-gray-500">
        {t("ownerFunds.list.loading")}
      </div>
    );

  if (error) return <EmptyState
    message={error.kind === "raw" ? error.text : t("ownerFunds.errors.loadList")}
    heading={t("directory.common.nothingHere")}
    backLabel={t("directory.common.goBack")}
  />;

  if (!rows.length)
    return (
      <EmptyState
        message={t("ownerFunds.list.empty")}
        heading={t("directory.common.nothingHere")}
        backLabel={t("directory.common.goBack")}
        showNewButton
        newButtonText={t("ownerFunds.list.postAdvanceWithdrawal")}
        newButtonPath={companyRoute(company_id, "/owner-funds/new")}
      />
    );

  const shownFrom = total === 0 ? 0 : (currentPage - 1) * ROWS_PER_PAGE + 1;
  const shownTo =
    total === 0 ? 0 : Math.min(currentPage * ROWS_PER_PAGE, total);

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center pt-4 pb-8 px-2`}
    >
      {/* Header (Apple style) */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-5xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={iconBtn}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-700" />
        </button>

        <div className="flex-1 flex justify-center pointer-events-none relative">
          <h2 className={`${A.title} text-center`}>
            {t("ownerFunds.title")}
          </h2>
        </div>

        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id, "/"))}
          className={iconBtn}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      {/* Summary + Search/New */}
      <div className="w-full max-w-5xl">
        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div className={`${A.card} p-4`}>
            <div className={A.label}>{t("ownerFunds.types.advance")}</div>
            <div
              className={`mt-1 text-[18px] font-semibold text-gray-700 ${A.num}`}
            >
              {money(balances.advance)}
            </div>
            <div className="mt-1 text-[11px] text-gray-400">
              {t("ownerFunds.list.liabilityHelp")}
            </div>
          </div>

          <div className={`${A.card} p-4`}>
            <div className={A.label}>{t("ownerFunds.types.withdrawal")}</div>
            <div
              className={`mt-1 text-[18px] font-semibold text-gray-700 ${A.num}`}
            >
              {money(balances.withdrawal)}
            </div>
            <div className="mt-1 text-[11px] text-gray-400">
              {t("ownerFunds.list.assetHelp")}
            </div>
          </div>
        </div>

        {/* Search & New */}
        <div className="flex items-center gap-3 mb-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-2.5 text-gray-400 pointer-events-none">
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                <circle
                  cx="11"
                  cy="11"
                  r="7"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  d="M20 20l-3-3"
                />
              </svg>
            </span>

            <input
              type="text"
              placeholder={t("ownerFunds.list.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={searchInputCls}
            />
          </div>

          <Button
            onClick={() =>
              navigate(companyRoute(company_id, "/owner-funds/new"))
            }
            className={btnSmCls}
            minWidth={btnSmMin}
          >
            {t("ownerFunds.list.post")}
          </Button>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-2">
          {paged.map((r) => (
            <div
              key={r.id}
              className={`${A.card} p-4 hover:bg-gray-50 transition cursor-pointer`}
              onClick={() => goToGJ(r.id, r.date)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") goToGJ(r.id, r.date);
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[13px] font-medium text-gray-700">
                    {formatAnyDateToLocale(r.date)}
                  </div>
                  <div className={`mt-0.5 ${A.label}`}>{r.bankName || "—"}</div>
                </div>

                <div className="text-right">
                  <div className={A.label}>{displayKind(r.kind)}</div>
                  <div
                    className={`mt-0.5 text-[13px] font-medium text-gray-700 ${A.num}`}
                  >
                    {money(r.amount)}
                  </div>
                </div>
              </div>

              {r.description ? (
                <div className="mt-2 text-[13px] text-gray-700 line-clamp-2">
                  {r.description}
                </div>
              ) : null}
            </div>
          ))}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block w-full max-w-5xl mx-auto">
          <ListTableShell>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className={`py-2 px-2 text-left ${A.label} font-medium`}>
                    {t("sales.common.date")}
                  </th>
                  <th className={`py-2 px-2 text-left ${A.label} font-medium`}>
                    {t("directory.products.type")}
                  </th>
                  <th className={`py-2 px-2 text-left ${A.label} font-medium`}>
                    {t("ownerFunds.list.bankCash")}
                  </th>
                  <th className={`py-2 px-2 text-right ${A.label} font-medium`}>
                    {t("sales.common.amount")}
                  </th>
                  <th className={`py-2 px-2 text-left ${A.label} font-medium`}>
                    {t("sales.edit.description")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {paged.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition"
                    onClick={() => goToGJ(r.id, r.date)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") goToGJ(r.id, r.date);
                    }}
                    style={{ outline: "none" }}
                    title={t("ownerFunds.list.openJournal")}
                  >
                    <td className="py-2 px-2">
                      {formatAnyDateToLocale(r.date)}
                    </td>
                    <td className="py-2 px-2">{displayKind(r.kind)}</td>
                    <td className="py-2 px-2">{r.bankName || "—"}</td>
                    <td className={`py-2 px-2 text-right ${A.num}`}>
                      {money(r.amount)}
                    </td>
                    <td className="py-2 px-2">{r.description || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ListTableShell>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-3 px-1">
            <span className={`${A.label} select-none`}>
              {t("directory.common.showing", { from: int(shownFrom), to: int(shownTo), total: int(total) })}
            </span>

            <div className="flex gap-2">
              <Button
                variant="tertiary" // ✅ tertiary button fix
                className={btnSmCls}
                minWidth={btnSmMin}
                disabled={currentPage === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t("directory.common.prev")}
              </Button>

              <Button
                variant="tertiary" // ✅ tertiary button fix
                className={btnSmCls}
                minWidth={btnSmMin}
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                {t("directory.common.next")}
              </Button>
            </div>
          </div>
        </div>

        {/* Pagination (mobile) */}
        <div className="md:hidden flex justify-between items-center mt-3 text-xs">
          <span className={`${A.label} select-none`}>
            {t("directory.common.showing", { from: int(shownFrom), to: int(shownTo), total: int(total) })}
          </span>

          <div className="flex gap-2">
            <Button
              variant="tertiary" // ✅ tertiary button fix
              className={btnSmCls}
              minWidth={btnSmMin}
              disabled={currentPage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t("directory.common.prev")}
            </Button>
            <Button
              variant="tertiary" // ✅ tertiary button fix
              className={btnSmCls}
              minWidth={btnSmMin}
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              {t("directory.common.next")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- derive Owner Advance/Withdrawal from General Journal ----------
function deriveOwnerFundsFromGJ(
  gj: JournalRow[],
  accountById: Map<number, Account>,
) {
  let advance = 0;
  let withdrawal = 0;
  const rowsOut: RowView[] = [];

  const groups = groupByEntry(gj);

  for (const g of groups) {
    const rowsWithAcc = g.group.map((r) => ({
      r,
      acc: accountById.get(r.account_id),
    }));

    // counterpart rows: ONLY advance + due_from (ignore capital)
    const cpRows = rowsWithAcc.filter(({ r, acc }) => {
      const kind = (acc?.kind || "").toLowerCase();
      if (["shareholder_advance", "due_from_shareholder"].includes(kind))
        return true;

      // fallback by name if account metadata is missing
      if (!acc && typeof r.account === "string")
        return fallbackKindFromName(r.account) !== "unknown";

      return false;
    });

    if (!cpRows.length) continue; // not an owner advance/withdrawal JE

    // bank rows (by kind or by name)
    const bankRows = rowsWithAcc.filter(({ r, acc }) => {
      const t = (acc?.type || "").toLowerCase();
      const k = (acc?.kind || "").toLowerCase();
      return (
        (acc && t === "asset" && (k === "bank" || k === "cash")) ||
        (!acc && typeof r.account === "string" && isBankName(r.account))
      );
    });

    const bankDebit = bankRows.reduce(
      (s, { r }) => s + Number(r.debit || 0),
      0,
    );
    const bankCredit = bankRows.reduce(
      (s, { r }) => s + Number(r.credit || 0),
      0,
    );

    // transaction type: prefer bank movement (most intuitive)
    let uiKind: RowView["kind"] = "unknown";
    if (bankDebit > 0 && bankCredit === 0) uiKind = "advance";
    else if (bankCredit > 0 && bankDebit === 0) uiKind = "withdrawal";
    else {
      // fallback: infer from counterpart account kind/name
      const kinds = new Set(
        cpRows
          .map(({ acc, r }) => {
            const k = (acc?.kind || "").toLowerCase();
            if (k === "shareholder_advance") return "advance";
            if (k === "due_from_shareholder") return "withdrawal";
            if (!acc && typeof r.account === "string")
              return fallbackKindFromName(r.account);
            return "unknown";
          })
          .filter((x) => x !== "unknown"),
      );
      if (kinds.has("advance")) uiKind = "advance";
      else if (kinds.has("withdrawal")) uiKind = "withdrawal";
    }

    // update balances (scan all rows in the group)
    for (const { r, acc } of rowsWithAcc) {
      const kind = (acc?.kind || "").toLowerCase();
      const name = typeof r.account === "string" ? r.account : undefined;

      const resolved: RowView["kind"] =
        kind === "shareholder_advance"
          ? "advance"
          : kind === "due_from_shareholder"
            ? "withdrawal"
            : fallbackKindFromName(name);

      const debit = Number(r.debit || 0);
      const credit = Number(r.credit || 0);

      if (resolved === "advance")
        advance += credit - debit; // liability normal credit
      else if (resolved === "withdrawal") withdrawal += debit - credit; // asset normal debit
    }

    // amount prefers bank movement; fallback to counterpart movement
    const cpDebit = cpRows.reduce((s, { r }) => s + Number(r.debit || 0), 0);
    const cpCredit = cpRows.reduce((s, { r }) => s + Number(r.credit || 0), 0);
    const amount = bankDebit || bankCredit || cpDebit || cpCredit || null;

    const bankName = bankRows.length
      ? bankRows[0].acc?.name ||
        (typeof bankRows[0].r.account === "string"
          ? bankRows[0].r.account
          : "—")
      : "—";

    rowsOut.push({
      id: g.entry_id,
      date: g.date,
      description: g.group[0].description || "",
      amount,
      bankName,
      kind: uiKind,
    });
  }

  return { derivedRows: rowsOut, bal: { advance, withdrawal } };
}
